import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Building2, User, Target, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { crmApi, errMsg, formatPhoneInput, fmtPhone, linkedinLabel } from './crmApi';

const Step = ({ icon: Icon, title, hint, children, testid }) => (
  <section className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={testid}>
    <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-violet-600" /><span className="text-sm font-semibold text-slate-800">{title}</span>{hint && <span className="text-[11px] text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 ml-auto">{hint}</span>}</div>
    {children}
  </section>
);
const F = ({ label, children }) => <div className="space-y-1"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;
const NEW = '__new__';
const EMPTY_CONTACT = { name: '', job_title: '', email: '', phone: '', phone_ext: '', linkedin: '' };

export const ConvertLeadDialog = ({ lead, open, onOpenChange, onConverted }) => {
  const [p, setP] = useState(null);
  const [name, setName] = useState('');
  const [rev, setRev] = useState('');
  const [contactId, setContactId] = useState('');
  const [nc, setNc] = useState(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !lead) return;
    setP(null); setRev(''); setNc(EMPTY_CONTACT);
    crmApi.convertPreview(lead.id).then(d => {
      setP(d); setName(d.suggested_name);
      setContactId(d.primary_contact ? d.primary_contact.id : (d.contacts[0]?.id || NEW));
    }).catch(() => toast.error('Could not load conversion details'));
  }, [open, lead]);
  const fixedContact = !!p?.primary_contact;
  const save = async () => {
    if (!name.trim()) return toast.error('Opportunity name is required');
    if (!fixedContact && contactId === NEW && !nc.name.trim()) return toast.error('Enter the contact name (or pick an existing contact)');
    setSaving(true);
    try {
      const body = { name: name.trim(), estimated_monthly_revenue: rev === '' ? null : Number(rev) };
      if (!fixedContact) { if (contactId === NEW) body.contact = nc; else body.contact_id = contactId; }
      const res = await crmApi.convertLead(lead.id, body);
      toast.success(res.contact_reused ? 'Lead converted — existing company and contact reused' : 'Lead converted');
      onConverted?.(res); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e) || 'Could not convert lead'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="convert-lead-dialog">
        <DialogHeader><DialogTitle>Convert Lead</DialogTitle></DialogHeader>
        {!p ? <div className="text-sm text-slate-500" data-testid="convert-loading">Loading…</div> : (
          <div className="space-y-3">
            <Step icon={Building2} title="Company" hint="Existing — reused" testid="convert-company">
              <div className="text-sm text-slate-800 font-medium" data-testid="convert-company-name">{p.company?.name}</div>
              <div className="text-xs text-slate-500">{[p.company?.city, p.company?.state].filter(Boolean).join(', ') || 'No location on file'}{p.company_open_opportunities > 0 && ` · ${p.company_open_opportunities} open opportunit${p.company_open_opportunities === 1 ? 'y' : 'ies'} already`}</div>
            </Step>
            <Step icon={User} title="Contact" hint={fixedContact ? 'Existing — reused' : (contactId !== NEW ? 'Existing — reused' : undefined)} testid="convert-contact">
              {fixedContact ? (
                <div className="text-sm text-slate-800" data-testid="convert-contact-name">{p.primary_contact.name}{p.primary_contact.job_title ? ` · ${p.primary_contact.job_title}` : ''}{p.primary_contact.email ? <span className="text-xs text-slate-500"> · {p.primary_contact.email}</span> : null}{p.primary_contact.phone ? <span className="text-xs text-slate-500"> · {fmtPhone(p.primary_contact.phone, p.primary_contact.phone_ext)}</span> : null}{p.primary_contact.linkedin ? <span className="text-xs text-slate-500"> · <a href={p.primary_contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:underline" data-testid="convert-contact-linkedin">{linkedinLabel(p.primary_contact.linkedin)}</a></span> : null}</div>
              ) : (
                <>
                  <select value={contactId} onChange={e => setContactId(e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="convert-contact-select">
                    {p.contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}</option>)}
                    <option value={NEW}>+ New contact</option>
                  </select>
                  {contactId === NEW && (
                    <div className="grid grid-cols-2 gap-2" data-testid="convert-new-contact">
                      <F label="Name *"><Input value={nc.name} onChange={e => setNc({ ...nc, name: e.target.value })} data-testid="convert-contact-new-name" /></F>
                      <F label="Job Title"><Input value={nc.job_title} onChange={e => setNc({ ...nc, job_title: e.target.value })} data-testid="convert-contact-new-title" /></F>
                      <F label="Email"><Input type="email" value={nc.email} onChange={e => setNc({ ...nc, email: e.target.value })} data-testid="convert-contact-new-email" /></F>
                      <F label="Phone"><Input value={nc.phone} onChange={e => setNc({ ...nc, phone: formatPhoneInput(e.target.value) })} placeholder="302-264-6219" data-testid="convert-contact-new-phone" /></F>
                      <F label="Ext."><Input value={nc.phone_ext} onChange={e => setNc({ ...nc, phone_ext: e.target.value.replace(/\D/g, '').slice(0, 8) })} data-testid="convert-contact-new-ext" /></F>
                      <F label="LinkedIn"><Input value={nc.linkedin} onChange={e => setNc({ ...nc, linkedin: e.target.value })} placeholder="linkedin.com/in/…" data-testid="convert-contact-new-linkedin" /></F>
                      <div className="col-span-2 text-[11px] text-slate-500">If this email or name already exists for {p.company?.name}, that contact is reused — no duplicate is created.</div>
                    </div>
                  )}
                </>
              )}
            </Step>
            <Step icon={Target} title="Opportunity" testid="convert-opportunity">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><F label="Name *"><Input value={name} onChange={e => setName(e.target.value)} data-testid="convert-opp-name" /></F></div>
                <F label="Est. $/month"><Input type="number" min="0" value={rev} onChange={e => setRev(e.target.value)} placeholder="optional" data-testid="convert-opp-revenue" /></F>
              </div>
              <div className="text-[11px] text-slate-500">Starts in <b>Qualified</b>. The lead becomes a read-only record with its full history kept.</div>
            </Step>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="convert-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !p || p.already_converted} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-submit"><CheckCircle2 className="h-4 w-4 mr-1" />{saving ? 'Converting…' : 'Convert Lead'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConvertLeadDialog;
